import { useEffect, useState } from 'react'
import _get from 'lodash/get'
import _replace from 'lodash/replace'
import _includes from 'lodash/includes'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import { Map, List, fromJS } from 'immutable'
import { useLazyQuery } from '@apollo/client'
import { getBoundActions } from '../../actions/index'
import { get as getConfig } from '../../config/index'
import { getPrimary } from '../../services/languages/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { LTM } from '../../services/currency/index'
import { JP, KR, IN } from '../../services/country/index'
import {
  PLAN_SKU_ANNUAL_SPECIAL,
  replaceSkuToPriceIncreasedSku,
  skuIsGift,
} from '../../services/plans/index'
import {
  cleanSku,
  userCheckoutAccountChangePlanAllowed,
} from '../../services/checkout/index'
import {
  formatBillingPlans,
} from '../../services/plans/utils'
import { GET_PLANS_BY_SKU } from '../../services/plans/query'
import { historyRedirect } from '../../services/navigation/index'
import {
  URL_ACCOUNT_CREATION,
  URL_CART_ACCOUNT_CREATION_CREATE,
  URL_UNAVAILABLE,
  URL_GIFT,
  URL_ACCOUNT_CHANGE_PLAN,
} from '../../services/url/constants'
import { getSeo } from '../../services/url/index'

const config = getConfig()
const giftEnabled = !!_get(config, ['features', 'gift', 'checkout'])
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

async function handlePlansSku (options = {}) {
  const { props, graphQLPlanData, graphQLPlanDataLoading } = options

  if (planGraphQLActive && (!graphQLPlanData || graphQLPlanDataLoading)) {
    return
  }

  let { sku } = options
  const {
    setPlansLocalizedSelection,
    setCheckoutPlanPreSelected,
    userAccount,
    history,
    plans,
    auth,
    setPageSeo,
    location,
    user = Map(),
    setPlansSelection,
  } = props
  const whitespaceRegex = /\s/g
  const cleanedSku = cleanSku(sku)
  const language = user.getIn(['data', 'language'])
  const userCountry = user.getIn(['data', 'requestCountry'])
  const isGiftSku = skuIsGift(sku)
  const isFallSpecialSku = cleanedSku === cleanSku(PLAN_SKU_ANNUAL_SPECIAL)
  const fallSpecialAllowedCountries = [JP, KR, IN]

  if (isGiftSku) {
    const giftPathname = `${URL_GIFT}/${cleanedSku}`
    const seo = getSeo({
      pathname: giftPathname,
      loggedIn: !!auth.get('jwt'),
    })
    historyRedirect({
      url: giftEnabled
        ? giftPathname
        : URL_UNAVAILABLE,
      language,
      history,
      auth,
    })
    setPageSeo({
      title: seo.title,
      description: seo.description,
      noFollow: seo.noFollow,
      noIndex: seo.noIndex,
      location,
    })
    return
  }

  if (
    (!planGraphQLActive
    && !plans.get('processing')
    && !plans.get('processingLocalized')
    && !plans.get('selection')
    && !plans.get('planError'))
    || (planGraphQLActive && graphQLPlanData.size)
  ) {
    const dataPlans = planGraphQLActive ? graphQLPlanData : plans.getIn(['data', 'plans'], List())
    const currencyIso = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'currencyIso'])
    const latamPricing = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'latamPricing'])
    const currency = latamPricing ? LTM : currencyIso
    // Get the translation data for this plan to help localization
    // strip spaces in the sku coming from the plan
    // and compare it to the sku coming in from the url
    const plan = dataPlans.find((item) => (
      _replace(item.get('sku'), whitespaceRegex, '').toLowerCase() === cleanedSku
    ))

    if (!getAuthIsLoggedIn(auth) && dataPlans.size === 0) {
      // wait to load plans - watcher in plans service should load these
      return
    }

    if (getAuthIsLoggedIn(auth)) {
      if (dataPlans.size === 0 || userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map()).size === 0) {
        // wait to load plans and userAccount details - watcher in plans service should load these
        return
      }

      // if the user is logged in and comes in with a sku that is in the allowed change plan skus
      // then redirect them to the account change plan area as opposed to the checkout flow,
      // as the checkout flow will deny them
      if (
        userCheckoutAccountChangePlanAllowed({ userAccount })
      ) {
        historyRedirect({
          url: `${URL_ACCOUNT_CHANGE_PLAN}?sku=${sku}`,
          language,
          history,
          auth,
        })

        return
      }
    }

    // If we find a plan use it instead of the sku since it has
    // translation data.
    if (plan) {
      sku = undefined
    }

    let planData = Map()

    if (!planGraphQLActive) {
      planData = await setPlansLocalizedSelection({
        sku, plan, language, currency,
      })

      if (!planData.getIn(['plans', 0, 'sku']) || !planData.getIn(['plans', 0, 'checkoutEnabled'])) {
        historyRedirect({
          url: URL_ACCOUNT_CREATION,
          language,
          history,
          auth,
        })
        return
      }
    }

    if (planGraphQLActive) {
      planData = plan

      // bail early if the user is trying to access the fall special sku
      // from a country that is not allowed
      if (isFallSpecialSku && !_includes(fallSpecialAllowedCountries, userCountry)) {
        historyRedirect({
          url: URL_ACCOUNT_CREATION,
          language,
          history,
          auth,
        })
        return
      }

      if (!Map.isMap(planData) || !planData.size || !planData.get('checkoutEnabled')) {
        historyRedirect({
          url: URL_ACCOUNT_CREATION,
          language,
          history,
          auth,
        })
        return
      }

      setPlansSelection(planData)
    }

    // for the Go sku flow,
    // set a value in state so we know the user came in with a pre-selected plan
    setCheckoutPlanPreSelected(true)

    // send the user to the second step of checkout when coming through the /go sku flow
    historyRedirect({
      url: URL_CART_ACCOUNT_CREATION_CREATE,
      language,
      history,
      auth,
    })
    return
  }

  if (planGraphQLActive) {
    // if we can't figure out what to do, send the user to the first step of checkout
    historyRedirect({
      url: URL_ACCOUNT_CREATION,
      language,
      history,
      auth,
    })
  }
}

function GoSku (props) {
  const {
    userAccount,
    location,
    user,
    auth,
  } = props

  const querySku = _get(location, ['query', 'sku'], '')
  const exactSku = replaceSkuToPriceIncreasedSku(querySku)
  const authJwt = auth.get('jwt')
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const userBillingSubscriptions = userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map()) || Map()
  let userAccountDataReady = true
  const [goPageUserLanguage, setGoPageUserLanguage] = useState(userLanguage)
  const [
    getPlansDataBySku,
    { data: planData, loading: planDataLoading },
  ] = useLazyQuery(GET_PLANS_BY_SKU)
  const formattedPlanData = fromJS(formatBillingPlans(planData))

  if (isLoggedIn && (!Map.isMap(userBillingSubscriptions) || !userBillingSubscriptions.size)) {
    userAccountDataReady = false
  }

  useEffect(() => {
    if (
      planGraphQLActive
        && querySku
        && userAccountDataReady
        && ((!planData && !planDataLoading) || (userLanguage !== goPageUserLanguage))
    ) {
      getPlansDataBySku({
        variables: {
          language: userLanguage,
          skus: [exactSku],
        },
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [userAccountDataReady, isLoggedIn, userLanguage])

  useEffect(() => {
    const { plans } = props

    if (!planGraphQLActive
      && process.env.BROWSER
      && (Map.isMap(plans) && plans.get('processing') === false)) {
      const newSku = replaceSkuToPriceIncreasedSku(querySku)

      handlePlansSku({
        props,
        sku: newSku,
      })
    }

    if (
      planGraphQLActive
      && process.env.BROWSER
      && planData && !planDataLoading
    ) {
      handlePlansSku({
        props,
        sku: exactSku,
        graphQLPlanData: formattedPlanData,
        graphQLPlanDataLoading: planDataLoading,
      })
    }
  })

  // set the user language for this page to help handle loading plans
  // if the user changes their language while on this page
  useEffect(() => {
    setGoPageUserLanguage(userLanguage)
  }, [userLanguage])

  return null
}

GoSku.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  setPageSeo: PropTypes.func.isRequired,
  setPlansLocalizedSelection: PropTypes.func.isRequired,
  setCheckoutPlanPreSelected: PropTypes.func.isRequired,
  setPlansSelection: PropTypes.func.isRequired,
}

export default connectRedux((state) => ({
  auth: state.auth,
  user: state.user,
  userAccount: state.userAccount,
  ...(!planGraphQLActive && { plans: state.plans }),
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setPlansLocalizedSelection: actions.plans.setPlansLocalizedSelection,
    setPlansSelection: actions.plans.setPlansSelection,
    setPageSeo: actions.page.setPageSeo,
    setCheckoutPlanPreSelected: actions.checkout.setCheckoutPlanPreSelected,
  }
})(GoSku)
